import axios from 'axios';
import { createAsyncThunk, isFulfilled, isPending, isRejected, createSlice } from '@reduxjs/toolkit';
import { cleanEntity } from 'app/shared/util/entity-utils';
import { createEntitySlice, IQueryParams, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { IProfesseur, defaultValue } from 'app/shared/model/professeur.model';

const initialState = {
  loading: false,
  errorMessage: null,
  entities: [] as ReadonlyArray<IProfesseur>,
  authorities: [] as any[],
  entity: defaultValue,
  updating: false,
  updateSuccess: false,
  totalItems: 0,
};

const apiUrl = 'api/professeurs';

// Actions

export const getProfesseurs = createAsyncThunk('professeurs/fetch_profs', async (entity: any) => {
  let requestUrl = '';
  let statutPresence;
  let typePersonnel;
  let matricule;
  let nom, prenom;
  let classe;
  let searchUrl = '';
  if (entity.matricule) {
    matricule = '&matricule=' + entity.matricule;
    searchUrl += matricule;
  }
  if (entity.nom) {
    nom = '&nom=' + entity.nom;
    searchUrl += nom;
  }
  if (entity.prenom) {
    prenom = '&prenom=' + entity.prenom;
    searchUrl += prenom;
  }
  if (entity.classe) {
    classe = '&classe=' + entity.classe;
    searchUrl += classe;
  }
  if (entity.statutPresence) {
    statutPresence = '&statutPresence=' + entity.statutPresence;
    searchUrl += statutPresence;
  }
  if (entity.typePersonnel) {
    typePersonnel = '&typePersonnel=' + entity.typePersonnel;
    searchUrl += typePersonnel;
  }
  if (!entity.matricule && !entity.nom && !entity.prenom && !entity.classe && !entity.statutPresence && !entity.typePersonnel) {
    requestUrl = `${apiUrl}?ecoleId=${entity.ecoleId}&page=${entity.page}&size=${entity.size}`;
  } else {
    requestUrl = `${apiUrl}?ecoleId=${entity.ecoleId}&page=${entity.page}&size=${entity.size}${searchUrl ? '&' : '?'}${searchUrl}`;
  }
  return axios.get<IProfesseur[]>(requestUrl);
});

export const getEcoleProfesseurs = createAsyncThunk(
  'professeur/fetch_ecole_profs',
  async (ecoleId: string) => {
    const requestUrl = `${apiUrl}/ecole/${ecoleId}`;
    return axios.get<IProfesseur[]>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const getRoles = createAsyncThunk('professeur/fetch_roles', async () => {
  return axios.get<any[]>(`api/authorities`);
});

export const getProfesseur = createAsyncThunk(
  'professeur/fetch_prof',
  async (id: string) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<IProfesseur>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createProfesseur = createAsyncThunk(
  'professeur/create_prof',
  async (entity: IProfesseur, thunkAPI) => {
    const result = await axios.post<IProfesseur>(`${apiUrl}/${entity.ecoleId}`, cleanEntity(entity));
    thunkAPI.dispatch(getProfesseurs(entity.ecoleId));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateProfesseur = createAsyncThunk(
  'professeur/update_prof',
  async (entity: IProfesseur, thunkAPI) => {
    const result = await axios.put<IProfesseur>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getProfesseurs(entity.ecoleId));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const validateNumeroPaiement = createAsyncThunk(
  'professeur/validate_numero_paiement_professeur',
  async (entity: IProfesseur, thunkAPI) => {
    const result = await axios.get<IProfesseur>(
      `${apiUrl}/${entity.numeroTelephonePaiement}/verify/${entity.validationKey}`,
      cleanEntity(entity)
    );
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const requestNewOtp = createAsyncThunk(
  'professeur/fetch_new_otp',
  async (id: string) => {
    const result = await axios.get<IProfesseur>(`${apiUrl}/${id}/otp`);
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteProfesseur = createAsyncThunk(
  'professeur/delete_prof',
  async (entity: IProfesseur, thunkAPI) => {
    const requestUrl = `${apiUrl}/${entity.id}`;
    const result = await axios.delete<IProfesseur>(requestUrl);
    thunkAPI.dispatch(getProfesseurs(entity.ecoleId));
    return result;
  },
  { serializeError: serializeAxiosError }
);

// slice

export type ProfesseurState = Readonly<typeof initialState>;

export const ProfesseurSlice = createSlice({
  name: 'professeur',
  initialState: initialState as ProfesseurState,
  reducers: {
    reset() {
      return initialState;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getRoles.fulfilled, (state, action) => {
        state.authorities = action.payload.data;
      })
      .addCase(getProfesseur.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteProfesseur.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = defaultValue;
      })
      .addMatcher(isFulfilled(getProfesseurs), (state, action) => {
        state.loading = false;
        state.entities = action.payload.data;
        state.totalItems = parseInt(action.payload.headers['x-total-count'], 10);
      })
      .addMatcher(isFulfilled(getEcoleProfesseurs), (state, action) => {
        state.loading = false;
        state.entities = action.payload.data;
        state.totalItems = parseInt(action.payload.headers['x-total-count'], 10);
      })
      .addMatcher(isFulfilled(createProfesseur, updateProfesseur, validateNumeroPaiement), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getProfesseurs, getProfesseur, getEcoleProfesseurs, validateNumeroPaiement), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createProfesseur, updateProfesseur, deleteProfesseur), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      })
      .addMatcher(
        isRejected(
          getProfesseurs,
          getEcoleProfesseurs,
          validateNumeroPaiement,
          getProfesseur,
          getRoles,
          createProfesseur,
          updateProfesseur,
          deleteProfesseur
        ),
        (state, action) => {
          state.loading = false;
          state.updating = false;
          state.updateSuccess = false;
          state.errorMessage = action.error.message;
        }
      );
  },
});

export const { reset } = ProfesseurSlice.actions;

// Reducer
export default ProfesseurSlice.reducer;
